// frontend/src/pages/Formatos.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import '../styles/Formatos.css';

const Formatos = () => {
  const { user, hasPermission } = useAuth();
  const [formatos, setFormatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [formatoEditando, setFormatoEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    categoria: 'solicitudes',
    version: '1.0',
    instrucciones: '',
    requiereFirma: false,
    archivo: null
  });

  const categorias = [
    { id: 'todos', nombre: 'Todos los formatos', icon: '' },
    { id: 'solicitudes', nombre: 'Solicitudes', icon: '' },
    { id: 'informes', nombre: 'Informes', icon: '' },
    { id: 'declaraciones', nombre: 'Declaraciones', icon: '' },
    { id: 'convenios', nombre: 'Convenios', icon: '' },
    { id: 'formatos_administrativos', nombre: 'Administrativos', icon: '' }
  ];

  useEffect(() => {
    cargarFormatos();
  }, [categoriaSeleccionada]);

  const cargarFormatos = async () => {
    try {
      setLoading(true);
      const url = categoriaSeleccionada !== 'todos' 
        ? `/formatos?categoria=${categoriaSeleccionada}`
        : '/formatos';
      
      const response = await api.get(url);
      setFormatos(response.data.data);
    } catch (error) {
      console.error('Error al cargar formatos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDescargar = async (formato) => {
    try {
      const response = await api.get(`/formatos/${formato._id}/descargar`, {
        responseType: 'blob'
      });

      // Crear URL del blob y descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', formato.nombre);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Mostrar mensaje de éxito
      alert('✅ Descarga iniciada');
    } catch (error) {
      console.error('Error al descargar:', error);
      alert('Error al descargar el formato');
    }
  };

  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      archivo: e.target.files[0]
    });
  };

  const handleSubmitFormato = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.categoria) {
      alert('Por favor, completa los campos obligatorios');
      return;
    }

    if (!formatoEditando && !formData.archivo) {
      alert('Por favor, selecciona un archivo');
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('nombre', formData.nombre);
      formDataToSend.append('descripcion', formData.descripcion);
      formDataToSend.append('categoria', formData.categoria);
      formDataToSend.append('version', formData.version);
      formDataToSend.append('instrucciones', formData.instrucciones);
      formDataToSend.append('requiereFirma', formData.requiereFirma);
      
      if (formData.archivo) {
        formDataToSend.append('archivo', formData.archivo);
      }

      if (formatoEditando) {
        await api.put(`/formatos/${formatoEditando._id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        alert('✅ Formato actualizado correctamente');
      } else {
        await api.post('/formatos', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        alert('✅ Formato creado correctamente');
      }

      // Reset y cerrar modal
      setShowAdminModal(false);
      setFormatoEditando(null);
      resetFormData();
      cargarFormatos();
    } catch (error) {
      console.error('Error al guardar formato:', error);
      alert('Error al guardar el formato');
    }
  };

  const handleEditar = (formato) => {
    setFormatoEditando(formato);
    setFormData({
      nombre: formato.nombre,
      descripcion: formato.descripcion || '',
      categoria: formato.categoria,
      version: formato.version || '1.0',
      instrucciones: formato.instrucciones || '',
      requiereFirma: formato.requiereFirma || false,
      archivo: null
    });
    setShowAdminModal(true);
  };

  const handleEliminar = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este formato?')) {
      try {
        await api.delete(`/formatos/${id}`);
        alert('✅ Formato eliminado');
        cargarFormatos();
      } catch (error) {
        console.error('Error al eliminar:', error);
        alert('Error al eliminar el formato');
      }
    }
  };

  const resetFormData = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      categoria: 'solicitudes',
      version: '1.0',
      instrucciones: '',
      requiereFirma: false,
      archivo: null
    });
  };

  const abrirModalNuevo = () => {
    setFormatoEditando(null);
    resetFormData();
    setShowAdminModal(true);
  };

  // Filtrar formatos por búsqueda
  const formatosFiltrados = formatos.filter(formato =>
    formato.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    formato.descripcion?.toLowerCase().includes(busqueda.toLowerCase())
  );

  const puedeAdministrar = hasPermission(['super_admin_jasp', 'admin_jasp']);

  return (
    <div className="formatos">
      <div className="formatos-header">
        <div>
          <h1>Formatos y Plantillas</h1>
          <p>Descarga los formatos oficiales para tus trámites</p>
        </div>
        {puedeAdministrar && (
          <button className="btn-primary" onClick={abrirModalNuevo}>
            + Nuevo Formato
          </button>
        )}
      </div>

      {/* Barra de búsqueda */}
      <div className="busqueda-section">
        <input
          type="text"
          placeholder="Buscar formatos..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="busqueda-input"
        />
      </div>

      {/* Categorías */}
      <div className="categorias-grid">
        {categorias.map(cat => (
          <button
            key={cat.id}
            className={`categoria-card ${categoriaSeleccionada === cat.id ? 'active' : ''}`}
            onClick={() => setCategoriaSeleccionada(cat.id)}
          >
            <span className="categoria-icon">{cat.icon}</span>
            <span className="categoria-nombre">{cat.nombre}</span>
          </button>
        ))}
      </div>

      {/* Lista de formatos */}
      <div className="formatos-list">
        {loading ? (
          <div className="text-center">Cargando formatos...</div>
        ) : formatosFiltrados.length === 0 ? (
          <div className="no-formatos">
            <p>No hay formatos disponibles en esta categoría</p>
          </div>
        ) : (
          <div className="formatos-grid">
            {formatosFiltrados.map(formato => (
              <div key={formato._id} className="formato-card">
                <div className="formato-icon">
                  {formato.categoria === 'solicitudes' && '📝'}
                  {formato.categoria === 'informes' && '📊'}
                  {formato.categoria === 'declaraciones' && '📑'}
                  {formato.categoria === 'convenios' && '🤝'}
                  {formato.categoria === 'formatos_administrativos' && '📁'}
                </div>
                
                <div className="formato-info">
                  <h3>{formato.nombre}</h3>
                  {formato.descripcion && <p>{formato.descripcion}</p>}
                  
                  <div className="formato-metadata">
                    {formato.version && (
                      <span className="badge">v{formato.version}</span>
                    )}
                    <span className="fecha">
                      📅 {new Date(formato.fechaPublicacion).toLocaleDateString()}
                    </span>
                    {formato.requiereFirma && (
                      <span className="badge badge-warning">Requiere firma</span>
                    )}
                  </div>

                  {formato.instrucciones && (
                    <div className="instrucciones">
                      <small>📋 {formato.instrucciones}</small>
                    </div>
                  )}
                </div>

                <div className="formato-actions">
                  <button
                    className="btn-descargar"
                    onClick={() => handleDescargar(formato)}
                    title="Descargar"
                  >
                    ⬇️ Descargar
                  </button>
                  
                  {puedeAdministrar && (
                    <>
                      <button
                        className="btn-icon"
                        onClick={() => handleEditar(formato)}
                        title="Editar"
                      >
                        ✏️
                      </button>
                      {hasPermission(['super_admin_jasp']) && (
                        <button
                          className="btn-icon"
                          onClick={() => handleEliminar(formato._id)}
                          title="Eliminar"
                        >
                          🗑️
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal para administrar formatos */}
      {showAdminModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-lg">
            <h2>{formatoEditando ? 'Editar Formato' : 'Nuevo Formato'}</h2>
            
            <form onSubmit={handleSubmitFormato}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Nombre del formato *</label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Categoría *</label>
                  <select
                    value={formData.categoria}
                    onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                    className="form-control"
                    required
                  >
                    {categorias.filter(c => c.id !== 'todos').map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Versión</label>
                  <input
                    type="text"
                    value={formData.version}
                    onChange={(e) => setFormData({...formData, version: e.target.value})}
                    className="form-control"
                    placeholder="1.0"
                  />
                </div>

                <div className="form-group">
                  <label>Requiere firma</label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.requiereFirma}
                      onChange={(e) => setFormData({...formData, requiereFirma: e.target.checked})}
                    />
                    Sí, este formato requiere firma
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                  className="form-control"
                  rows="2"
                  placeholder="Breve descripción del formato"
                />
              </div>

              <div className="form-group">
                <label>Instrucciones</label>
                <textarea
                  value={formData.instrucciones}
                  onChange={(e) => setFormData({...formData, instrucciones: e.target.value})}
                  className="form-control"
                  rows="3"
                  placeholder="Instrucciones para llenar el formato"
                />
              </div>

              <div className="form-group">
                <label>Archivo *</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="form-control"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                />
                {!formatoEditando && (
                  <small className="text-muted">
                    Formatos permitidos: PDF, Word, Excel, texto (máx. 20MB)
                  </small>
                )}
                {formatoEditando && (
                  <small className="text-muted">
                    Deja vacío si no quieres cambiar el archivo actual
                  </small>
                )}
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowAdminModal(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {formatoEditando ? 'Actualizar' : 'Crear'} Formato
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Formatos;